import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BalanceRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { longDt } from '../lib/filters';

/**
 * The shift-close sheet — the legacy `printbalance`, printed after Uzavřít
 * směnu and signed by the people handing over: who was on, the till by
 * channel, the cash-and-card total ("v kapsičce"), the note, and the
 * signature lines. Field for field the legacy sheet; reads
 * /api/balances/:id, writes nothing.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;

export function UzaverkaTisk() {
  const { id } = useParams();
  const bid = Number(id);
  const [sp] = useSearchParams();
  const preview = sp.get('tisk') === '0';
  const q = useQuery({ queryKey: ['balance', bid], queryFn: () => api.balance(bid), enabled: Number.isInteger(bid) && bid > 0, retry: false });
  useEffect(() => {
    if (!q.data || preview) return;
    const t = window.setTimeout(() => window.print(), 250);
    return () => window.clearTimeout(t);
  }, [q.data, preview]);

  if (!Number.isInteger(bid) || bid <= 0) return <div className="container state">Neplatné číslo uzávěrky.</div>;
  if (q.isPending) return <div className="container state">Připravuji uzávěrku…</div>;
  if (q.isError || !q.data) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return <div className="container state">{notFound ? `Uzávěrka ${bid} neexistuje.` : 'Uzávěrku se nepodařilo načíst.'}</div>;
  }
  const b = q.data as BalanceRow;
  const staff = [b.closedByName, ...b.staff.map((s) => s.name).filter((n) => n !== b.closedByName)].filter(Boolean);
  const sig = <span className="fillbox">&nbsp;</span>;

  return (
    <div className="protokol uzaverka">
      <div className="controls noprint">
        <button type="button" className="btn" onClick={() => window.print()}>
          {preview ? 'Tisk' : 'Opakovat tisk'}
        </button>
        <button type="button" className="btn" onClick={() => window.close()}>
          Zavřít okno
        </button>
      </div>
      <div className="sheet">
        <h2>
          Uzávěrka ID: {b.id} · Směna: {b.kind === 'day' ? 'denní' : b.kind === 'night' ? 'noční' : 'jiná'}
        </h2>
        <p>
          {longDt(b.started)} – {longDt(b.closed)}
        </p>
        <div className="box">
          <p>
            <strong>Přítomni na směně:</strong> {staff.join(', ') || '—'}
          </p>
          <p className="gap">Podpis: {sig}</p>
          <p className="gap">
            <strong>Přítomni při odhozu:</strong> {sig}
          </p>
          <p>Podpis: {sig}</p>
        </div>
        <table>
          <tbody>
            <tr>
              <th>Hotovost</th>
              <td className="num">{kc(b.till.cash)}</td>
            </tr>
            <tr>
              <th>Karta</th>
              <td className="num">{kc(b.till.card)}</td>
            </tr>
            <tr>
              <th>GoPay</th>
              <td className="num">{kc(b.till.gopay)}</td>
            </tr>
            <tr>
              <th>Fakt. CK</th>
              <td className="num">{kc(b.till.invoice)}</td>
            </tr>
            {b.till.onsiteOther > 0 && (
              <tr>
                <th>Automat</th>
                <td className="num">{kc(b.till.onsiteOther)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th>Celkem v kapsičce (hotovost + karta)</th>
              <td className="num">{kc(b.till.cash + b.till.card)}</td>
            </tr>
            <tr>
              <th>Celkem</th>
              <td className="num">{kc(b.till.total)}</td>
            </tr>
          </tfoot>
        </table>
        <p className="small">Základ: {b.till.cars} vozů, které odjely během směny.</p>
        <div className="box note">
          <p>
            <strong>Poznámka:</strong> {b.note || '—'}
          </p>
        </div>
        <p className="gap">Předání směny: {sig}</p>
        <p className="gap">Podpis přebírajícího: {sig}</p>
        <p className="gap">Úklid: Mercedes, myčka, kancelář {sig}</p>
        <p className="gap">Kontrola uzávěrky: {sig}</p>
      </div>
    </div>
  );
}
